/**
 * Workbook overview page.
 *
 * Loads workbook meta, all sheet bindings (one query), and all mapping
 * profiles in parallel, then renders SheetBindingForm for per-sheet
 * interaction.
 */

import { useParams } from "react-router";
import { ApiError } from "@/lib/api/client";
import { PageError, PageLoading, useLoader } from "@/lib/loader";
import { NotFoundPage } from "@/pages/NotFoundPage";
import {
  getExportJobDownloadUrl,
  getWorkbook,
  listExportJobs,
  listProfiles,
} from "@/lib/api/endpoints";
import type { ParsedSheet, Detection } from "@/lib/mapper/types";
import { ExportStatusBadge } from "@/pages/ExportStatusBadge";
import {
  SheetBindingForm,
  type WorkbookSummary,
  type BindingSummary,
  type ProfileSummary,
} from "./SheetBindingForm";


function formatRelative(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  const diff = Date.now() - date.getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return date.toLocaleDateString();
}

export function WorkbookPage() {
  const { id = "" } = useParams();
  const loaded = useLoader(
    () =>
      Promise.all([
        getWorkbook(id),
        listProfiles(),
        listExportJobs({ workbookId: id, limit: 10 }),
      ]),
    [id],
  );
  if (loaded.status === "error") {
    if (loaded.error instanceof ApiError && loaded.error.status === 404) return <NotFoundPage />;
    return <PageError error={loaded.error} />;
  }
  if (!loaded.data) return <PageLoading />;
  const [data, profiles, exportJobs] = loaded.data;

  const { workbook, bindings } = data;

  // Coerce Prisma Json → typed shapes.
  const sheets = workbook.sheets as unknown as ParsedSheet[];
  const detected = workbook.detected as unknown as Record<
    string,
    Detection
  >;

  // Build plain-object summaries safe to pass to the client island.
  const workbookSummary: WorkbookSummary = {
    id: workbook.id,
    originalName: workbook.originalName,
    sheets,
    detected,
    createdAt: workbook.createdAt,
  };

  const bindingSummaries: BindingSummary[] = bindings.map((b) => ({
    sheetName: b.sheetName,
    profileId: b.profileId ?? null,
    profile: b.profile
      ? {
          id: b.profile.id,
          name: b.profile.name,
          productType: b.profile.productType,
          type: b.profile.type,
          isDefault: b.profile.isDefault,
        }
      : null,
  }));

  const profileSummaries: ProfileSummary[] = profiles.map((p) => ({
    id: p.id,
    name: p.name,
    productType: p.productType,
    type: p.type,
    isDefault: p.isDefault,
  }));

  return (
    <>
      <SheetBindingForm
        workbook={workbookSummary}
        bindings={bindingSummaries}
        profiles={profileSummaries}
      />

      <section className="mx-auto max-w-7xl px-5 pb-8 lg:px-6">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
          Export history
        </h2>
        {exportJobs.length === 0 ? (
          <div className="rounded-lg border border-dashed border-zinc-300 dark:border-border p-8 text-center text-sm text-zinc-500 dark:text-zinc-400">
            No exports yet for this workbook.
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100 dark:divide-zinc-800 rounded-lg border border-zinc-200 dark:border-border bg-white dark:bg-surface">
            {exportJobs.map((job) => {
              return (
                <li
                  key={job.id}
                  className="flex items-center justify-between gap-3 px-4 py-3 text-sm"
                >
                  <div className="min-w-0">
                    <div className="font-mono text-xs text-zinc-700 dark:text-zinc-300">
                      export-{job.id.slice(0, 8)}
                    </div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400">
                      {formatRelative(job.createdAt)}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <ExportStatusBadge status={job.status} />
                    {job.done && (
                      <a
                        href={getExportJobDownloadUrl(job.id)}
                        className="text-xs font-medium text-zinc-700 dark:text-zinc-300 hover:underline"
                      >
                        Download
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </>
  );
}
